/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo } from 'react'
import { Layout, Tooltip } from 'antd'
import { useLocation } from 'react-router-dom'
import colors from '@sportninja/common/constants/appColors'
import SidebarMenu from './SidebarMenu'
import useWindowSize from 'src/hooks/useWindowSize'
import MenuHamburgerIcon from '../icons/MenuHamburgerIcon'
import MenuOpenIcon from '../icons/MenuOpenIcon'

const { Sider } = Layout

type CollapseType = 'clickTrigger' | 'responsive'

interface SidebarProps {
  collapsed: boolean
  onCollapse: (collapsed: boolean, type: CollapseType) => void
}

/**
 * Sidebar component with collapsible navigation menu
 * Integrates with Ant Design Layout.Sider
 */
const Sidebar: React.FC<SidebarProps> = ({ collapsed, onCollapse }) => {
  const location = useLocation()
  const isPaymentPage = location?.pathname?.includes('pay')
  const isRegistrationInvitePage =
    location?.pathname?.startsWith('/registration/') &&
    !location?.pathname?.startsWith('/registration-details/')
  const isLoginPage = location?.pathname?.includes('/login')
  const isRegisterPage = location?.pathname?.includes('/signup')
  const isForgotPasswordPage = location?.pathname?.includes('/forgot-password')
  const isVerifyEmailPage = location?.pathname?.includes('/verify-email')
  const isPasswordResetPage = location?.pathname?.includes('/password-reset')
  const isTestPage = location?.pathname?.includes('/ant-test')
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width < 768, [width])

  useEffect(() => {
    if (isMobile) {
      onCollapse(true, 'clickTrigger')
    }
  }, [isMobile])

  if (
    isPaymentPage ||
    isLoginPage ||
    isRegistrationInvitePage ||
    isRegisterPage ||
    isForgotPasswordPage ||
    isVerifyEmailPage ||
    isPasswordResetPage ||
    isTestPage
  ) {
    return null
  }

  const effectiveCollapsed = isMobile ? true : collapsed
  const siderWidth = effectiveCollapsed ? 64 : 284

  return (
    <>
      <Sider
        collapsible
        collapsed={effectiveCollapsed}
        onCollapse={onCollapse}
        trigger={null}
        width={284}
        collapsedWidth={64}
        breakpoint='md'
        style={{
          overflowX: 'hidden',
          overflowY: 'auto',
          position: 'fixed',
          left: 0,
          top: 48, // Header height
          bottom: 0,
          zIndex: 10,
          paddingTop: 16,
          transition: 'width 0.2s ease-in-out',
          backgroundColor: '#212935',
          borderRight: '1px solid #3A4659',
        }}
        theme='dark'
      >
        <SidebarMenu
          collapsed={collapsed}
          currentPath={location.pathname}
          onExpand={() => onCollapse(false, 'clickTrigger')}
          isMobile={isMobile}
        />
      </Sider>
      {!isMobile && (
        <Tooltip title={collapsed ? 'Expand' : 'Collapse'} placement='right'>
          <button
            type='button'
            onClick={() => onCollapse(!collapsed, 'clickTrigger')}
            css={{
              position: 'fixed',
              left: siderWidth,
              top: 72,
              transform: 'translateX(-50%)',
              transition: 'left 0.2s ease-in-out',
              zIndex: 11,
              background: '#212935',
              border: `1px solid ${colors.SECONDARY_100}`,
              borderRadius: 24,
              padding: 6,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                background: colors.SECONDARY_400,
              },
            }}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? (
              <MenuHamburgerIcon size={18} color={colors.NEUTRAL_50} />
            ) : (
              <MenuOpenIcon size={18} color={colors.NEUTRAL_50} />
            )}
          </button>
        </Tooltip>
      )}
    </>
  )
}

export default Sidebar
